'use client';

import { intlFormat } from 'date-fns';

import { useLocale, useTranslations } from 'next-intl';

import slugify from 'slugify';

import { Icons } from '@/app/_components/ui/icons';
import Tag from '@/app/_components/ui/tag';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/_components/ui/tooltip';
import generatePath from '@/lib/router/generate-path';
import Routes from '@/lib/router/routes';
import Link from '@/lib/translation/components/link';
import { cn } from '@/lib/utils';

import { GenericDocument } from './types';

interface DocumentUiProps {
  document: GenericDocument;
  onOpen?: (documentId: string) => void;
  className?: string;
}

const DocumentUi = ({ document, onOpen, className }: DocumentUiProps) => {
  const t = useTranslations();
  const locale = useLocale();

  const { id, title, important, isNew, addDate } = document;

  return (
    <Link
      href={
        id
          ? `${generatePath(Routes.LegalEntityDocument, { id })}?filename=${slugify(title)}`
          : ''
      }
      download={title}
      target="_blank"
      className={cn(
        'flex h-20 items-center gap-4 duration-500 hover:-translate-y-1 [&>*:first-child]:hover:shadow-lg',
        className,
      )}
      onClick={id && onOpen ? () => onOpen(id) : undefined}
      rel="nofollow"
    >
      <div
        className={cn(
          'relative shrink-0 grow-0 rounded-2xl p-5 duration-500',
          important ? 'bg-primary-50' : 'border border-muted-foreground/50',
          isNew
            ? 'after:absolute after:right-0 after:top-0 after:size-4 after:-translate-y-1/4 after:translate-x-1/4 after:rounded-full after:border-2  after:border-white after:bg-primary'
            : '',
        )}
      >
        <Icons.File
          className={cn(
            'size-6',
            important ? 'text-primary' : 'text-muted-foreground',
          )}
        />
      </div>

      <div className="flex flex-col justify-around overflow-hidden">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              {important ? (
                <div className="flex items-center gap-2 font-bold">
                  <div className="flex-1 truncate font-bold">{title}</div>
                  <Tag content={t('common.important').toUpperCase()} />
                </div>
              ) : (
                <div className="truncate font-bold text-muted-foreground">
                  {title}
                </div>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {addDate ? (
          <div className="flex-1 truncate text-sm text-muted-foreground">
            {t('pages.project.documents.campaign.date', {
              date: intlFormat(addDate, { locale }),
            })}
          </div>
        ) : null}
      </div>
    </Link>
  );
};

export default DocumentUi;
